import React, { useEffect, useCallback, useMemo } from 'react'
import useDashboard from './useDashboard'
import { useUIGeneralContext } from '../contextProviders/uiGeneralContext'
import { parseDateWithTimeZone, compareDateStrings } from '@aytm/app/insights/dashboard/lib/datetime'
import { safelyExecuteSync } from '@aytm/app/insights/dashboard/lib/safety'
import { createCacheWithParameter } from '@aytm/app/insights/dashboard/lib/cache'

interface TimeZoneInfo {
  title: string
  offset: string
}

const withFormattedTimeCache = createCacheWithParameter<Intl.DateTimeFormat, Date, string>(
  100,
  (d1, d2) => d1?.getTime() === d2?.getTime()
)

export const useDateTime = () => {
  const {
    userTimeZone: { title, offset }
  }: { userTimeZone: TimeZoneInfo } = useDashboard()
  const { currentFormatters: formatters, setTimeZoneTitle, setTimeZoneOffset, timeZoneTitle } = useUIGeneralContext()

  useEffect(() => {
    setTimeZoneTitle(title)
  }, [setTimeZoneTitle, title])

  useEffect(() => {
    setTimeZoneOffset(offset)
  }, [offset, setTimeZoneOffset])

  const parseDateTime = useCallback((input: string) => parseDateWithTimeZone(input, offset), [offset])

  const formatDateInstance = useCallback(
    (formatter: Intl.DateTimeFormat, date: Date | string | number) => {
      const resolvedDate =
        ('string' === typeof date ? parseDateTime(date) : 'number' === typeof date ? new Date(date) : date) ||
        new Date(date)

      if (Number.isNaN(resolvedDate.getTime())) {
        return ''
      }
      return formatter
        ? withFormattedTimeCache(formatter, resolvedDate, arg => formatter.format(arg))
        : resolvedDate.toString()
    },
    [parseDateTime]
  )

  const toInputElementValue = useCallback(
    //2023-04-12T21:00:00
    (input: Date | string | number) =>
      formatDateInstance(formatters.dateTimeForInputFormatter, input).replace(
        /^(\d{4})-(\d{2})-(\d{2}),\s(\d{2}):(\d{2})(:(\d{2}))?$/,
        '$1-$2-$3T$4:$5:00'
      ),
    [formatDateInstance, formatters.dateTimeForInputFormatter]
  )

  const toDateParts = useCallback(
    (input: Date | string | number) =>
      formatDateInstance(formatters.dateFormatter, input)
        .replace(/^(\w{3})\s+(\d{1,2}),\s+(\d{4})$/, '$3-$1-$2')
        .split('-'),
    [formatDateInstance, formatters.dateFormatter]
  )

  const compareDates = useCallback(
    (a: Date, b: Date) => compareDateStrings(toInputElementValue(a), toInputElementValue(b)),
    [toInputElementValue]
  )

  const extendRange = useCallback(
    (start: Date | string | number, end: Date | string | number, percent: number) => {
      const resolvedStart =
        'string' === typeof start ? parseDateTime(start) : 'number' === typeof start ? new Date(start) : start
      const resolvedEnd = 'string' === typeof end ? parseDateTime(end) : 'number' === typeof end ? new Date(end) : end

      const diff = resolvedEnd.getTime() - resolvedStart.getTime()
      const newStart = resolvedStart.getTime() - (diff * percent) / 2
      const newEnd = resolvedEnd.getTime() + (diff * percent) / 2
      return [newStart, newEnd]
    },
    [parseDateTime]
  )

  const basicAPI = useMemo(
    () =>
      ({
        parseDateTime,
        compareDates,
        toDate: formatDateInstance.bind(null, formatters.dateFormatter),
        toLongDateTime: formatDateInstance.bind(null, formatters.longDateTimeFormatter),
        toTimeWithSeconds: formatDateInstance.bind(null, formatters.timeWithSecondsFormatter),
        toTimeWithoutSeconds: formatDateInstance.bind(null, formatters.timeWithoutSecondsFormatter),
        timeZoneName: title,
        timeZoneOffset: offset,
        toInputElementValue,
        extendRange
      }) as const,
    [parseDateTime, compareDates, formatters, formatDateInstance, title, offset, toInputElementValue, extendRange]
  )

  const renderDate = useCallback(
    (date: Date | string | number, className?: string) => {
      const resolvedDate =
        'string' === typeof date ? parseDateTime(date) : 'number' === typeof date ? new Date(date) : date

      const title = safelyExecuteSync<string>(() => formatters.longDateTimeFormatter.format(resolvedDate), null)
      const children = safelyExecuteSync(() => formatters.dateFormatter.format(resolvedDate), null)
      return (
        <span className={className} title={`${title}\nTimezone: ${timeZoneTitle}`}>
          {children}
        </span>
      )
    },
    [formatters, parseDateTime, timeZoneTitle]
  )

  const renderDateRange = useCallback(
    (start: Date | string | number, end: Date | string | number, className?: string) => {
      if (start === end) {
        return renderDate(start)
      }

      const resolvedFrom =
        'string' === typeof start ? parseDateTime(start) : 'number' === typeof start ? new Date(start) : start

      const resolvedTo = 'string' === typeof end ? parseDateTime(end) : 'number' === typeof end ? new Date(end) : end

      const renderedFrom = safelyExecuteSync(() => formatters.dateFormatter.format(resolvedFrom), null)
      const renderedTo = safelyExecuteSync(() => formatters.dateFormatter.format(resolvedTo), null)

      const renderedFullFrom = safelyExecuteSync<string>(
        () => formatters.longDateTimeFormatter.format(resolvedFrom),
        null
      )
      const renderedFullTo = safelyExecuteSync<string>(() => formatters.longDateTimeFormatter.format(resolvedTo), null)

      // If years are the same, months are the same, and days are the same, we only show like: `Mar 12, 2023`
      // If years are the same, and months are the same, but the days are not,  we show like: `Mar 12-15, 2023`
      // If years are the same, but months are not, we show like: `Mar 12 - Apr 15, 2023`

      const [fromYear, fromMonth, fromDate] = toDateParts(resolvedFrom)
      const [toYear, toMonth, toDate] = toDateParts(resolvedTo)

      if (fromYear === toYear) {
        const shownText =
          fromMonth === toMonth
            ? fromDate === toDate
              ? renderedFrom
              : `${fromMonth} ${fromDate}-${toDate}, ${fromYear}`
            : `${fromMonth} ${fromDate} - ${toMonth} ${toDate}, ${fromYear}`

        return (
          <span
            className={className}
            title={`From: ${renderedFullFrom}\nto: ${renderedFullTo}\nTimezone: ${timeZoneTitle}`}
          >
            {shownText}
          </span>
        )
      }

      return (
        <>
          <span className={className} title={renderedFullFrom}>
            {renderedFrom}
          </span>{' '}
          -{' '}
          <span className={className} title={renderedFullTo}>
            {renderedTo}
          </span>
        </>
      )
    },
    [formatters, parseDateTime, renderDate, timeZoneTitle, toDateParts]
  )

  return Object.assign(basicAPI, { renderDate, renderDateRange } as const)
}
